import * as R from 'ramda'
import { JoinRequest } from '@weblite/ts-action/hall'
// @ts-ignore
import { createAction } from 'redux-actions'
import { Admin, BannedUser, Subscriber } from '@weblite/ts-action/subscription'
import { dispatch } from '$setup/redux'

export type OpenedUserListItem = Subscriber | Admin | BannedUser | JoinRequest

export const DEFAULT_OPEN_LIST_TYPE = 'common'
export const JOIN_HALL_REQUESTS_STORAGE_KEY = 'joinHallRequests'

const getOpenedUserListId = (item: OpenedUserListItem) => {
  if ('id' in item) return item.id
  if ('userId' in item) return item.userId
  return ''
}

interface StateItem {
  users: { [userId: string]: OpenedUserListItem }
  searchedIds: { [userId: string]: true }
}

type State = Record<string, StateItem>

const initialState: State = {
  common: {
    users: {},
    searchedIds: {},
  },
  [JOIN_HALL_REQUESTS_STORAGE_KEY]: {
    users: {},
    searchedIds: {},
  },
}

// actions
const OPENED_USER_LIST_RESET = 'OPENED_USER_LIST_RESET'
const openedUserListReset = createAction(OPENED_USER_LIST_RESET)

const OPENED_USER_LIST_RESET_SEARCH = 'OPENED_USER_LIST_RESET_SEARCH'
const openedUserListResetSearch = createAction(OPENED_USER_LIST_RESET_SEARCH)

const OPENED_USER_LIST_INSERT_BATCH = 'OPENED_USER_LIST_INSERT_BATCH'
const openedUserListInsertBatch = createAction(OPENED_USER_LIST_INSERT_BATCH)

const OPENED_USER_LIST_REMOVE = 'OPENED_USER_LIST_REMOVE'
const openedUserListRemove = createAction(OPENED_USER_LIST_REMOVE)

const OPENED_USER_LIST_MODIFY = 'OPENED_USER_LIST_MODIFY'
const openedUserListModify = createAction(OPENED_USER_LIST_MODIFY)

export const openedUserListStorage = {
  insertBatch: (users: OpenedUserListItem[], key: string) =>
    dispatch(openedUserListInsertBatch({ users, key })),

  remove: (userId: string, key: string) =>
    dispatch(openedUserListRemove({ userId, key })),

  modify: (user: Partial<OpenedUserListItem>, key: string) =>
    dispatch(openedUserListModify({ user, key })),

  reset: (key: string) => dispatch(openedUserListReset(key)),
  resetSearch: (key: string) => dispatch(openedUserListResetSearch(key)),
}

// reducers
const reducers = {
  [OPENED_USER_LIST_RESET]: (state: State, key: string) => ({
    ...state,
    [key]: {
      users: {},
      searchedIds: {},
    },
  }),

  [OPENED_USER_LIST_RESET_SEARCH]: (state: State, key: string) => ({
    ...state,
    [key]: {
      users: state[key].users,
      searchedIds: {},
    },
  }),

  [OPENED_USER_LIST_REMOVE]: (
    state: State,
    { userId, key }: { userId: string; key: string },
  ) => ({
    ...state,
    [key]: R.evolve(
      {
        users: R.dissoc(userId),
        searchedIds: R.dissoc(userId),
      },
      state[key],
    ),
  }),

  [OPENED_USER_LIST_MODIFY]: (
    state: State,
    { key, user }: { key: string; user: Partial<OpenedUserListItem> },
  ) => {
    const id = getOpenedUserListId(user)
    const { id: _, userId: _1, ...modifyObject } = user
    return {
      ...state,
      [key]: R.assocPath(
        ['users', id],
        R.mergeRight(state[key].users[id], modifyObject),
        state[key],
      ),
    }
  },

  [OPENED_USER_LIST_INSERT_BATCH]: (
    state: State,
    { users, key }: { users: OpenedUserListItem[]; key: string },
  ) => {
    return {
      ...state,
      [key]: R.reduce(
        (acc, user) => {
          const id = getOpenedUserListId(user)

          return R.evolve(
            {
              users: R.assoc(id, user),
              searchedIds: R.assoc(id, true),
            },
            acc,
          )
        },
        state[key] || initialState.common,
        users,
      ),
    }
  },
}

export const openedUserListReducer = (
  state = initialState,
  { type, payload }: { type: keyof typeof reducers; payload: any },
) => (reducers[type] ? reducers[type](state, payload) : state)
